import { ConnectQueryKeys } from '~/connect/api';
import {
  CrudOptions,
  OverridableQuery,
  UseQueryOptions,
  serialize,
  useCrud,
  useQuery,
  useMutation,
  UseMutationOptions,
  restrictApi,
} from '@wyz/utils';
import { useApi } from '~/common/context';
import {
  CustomerCriterion,
  ICriteriaUsage,
  ICriteriaUsageVariable,
  ISettingTemplate,
  ISettingTemplatePayload,
} from '~/connect/types';
import {
  IErrorApiResponse,
  ISuccessApiResponse,
  IValidationError,
} from '@wyz/types';
import { useConfig } from '~/common/context';
import { CommonErrorCodesEnumType } from '~/common/types';
import { isNil } from 'lodash-es';

type Actions = 'create' | 'update' | 'getOneById' | 'remove';

type Options = Pick<
  CrudOptions<
    ISettingTemplate,
    ISettingTemplatePayload,
    ISettingTemplatePayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >
  >,
  Actions
> & {
  list?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<ISettingTemplate>>,
      IErrorApiResponse
    >
  >;

  getMatched?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<ISettingTemplate | null>,
      IErrorApiResponse<unknown, CommonErrorCodesEnumType>
    >
  > & {
    args?: {
      customerCriteria: Array<CustomerCriterion>;
      customerInternalCriteria: Array<CustomerCriterion>;
    };
  };
  checkCriteriaUsage?: UseMutationOptions<
    ISuccessApiResponse<ICriteriaUsage>,
    IErrorApiResponse,
    ICriteriaUsageVariable
  >;
};

export const useManageSettingTemplates = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const api = useCrud<
    Actions,
    ISettingTemplate,
    ISettingTemplatePayload,
    ISettingTemplatePayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >
  >(
    {
      baseKey: ConnectQueryKeys.settingTemplates.base(),
      caller: callCoreApi,
      endpoint: `/tenants/api/wtb/tenants/${tenantCode}/setting-templates`,
    },
    {
      ...options,
      update: {
        ...options.update,
        keysToInvalidateOnSuccess: [
          ConnectQueryKeys.settingTemplates.base(),
          ConnectQueryKeys.users.base(),
        ],
      },
    },
  );

  const getMatchedQuery = useQuery({
    queryKey: ConnectQueryKeys.settingTemplates.match(options.getMatched?.args),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<ISettingTemplate | null>>(
        `/tenants/api/wtb/tenants/${tenantCode}/setting-templates/matched?${serialize(
          options.getMatched?.args,
        )}`,
      ),
    ...options.getMatched,
    enabled:
      !isNil(options.getMatched?.args?.customerCriteria) &&
      Boolean(options.getMatched.enabled),

    retry: false,
    gcTime: 0,
  });

  const checkCriteriaUsage = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse<ICriteriaUsage>>(
        `/tenants/api/wtb/tenants/${tenantCode}/setting-templates/${
          variables.id
        }/check-usage?${serialize(variables.query)}`,
      ),
    ...options.checkCriteriaUsage,
  });

  return restrictApi(options, {
    ...api,
    getMatched: getMatchedQuery,
    checkCriteriaUsage,
  });
};
